import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSkeleton,
  Modal,
  Select,
} from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../../shared/utils/format';
import type { ContractRenewal, RenewalInput } from './types';
import {
  useApproveRenewal,
  useCreateRenewal,
  useRejectRenewal,
  useRenewalPipeline,
} from './useContractQueries';

/* ── Fluxo de Renovação de Contratos (R2C, tela 8)
 * Consome GET /contracts/renewals/pipeline (alertas de expiração, pipeline
 * de renovação e propostas de ajuste financeiro), POST /contracts/:id/renewals
 * (nova proposta) e POST /renewals/:id/approve|reject (decisão).
 * Protótipo: Expiration Alerts, Renewal Pipeline, Financial Adjustment
 * Proposal (Vendor/Contract, Index, Current Value, ...).
 */

const RENEWAL_STATUS_LABEL: Record<string, string> = {
  PROPOSED: 'Proposta',
  APPROVED: 'Aprovada',
  REJECTED: 'Rejeitada',
  EXECUTED: 'Executada',
};

const RENEWAL_STATUS_VARIANT: Record<string, 'info' | 'success' | 'error' | 'neutral'> = {
  PROPOSED: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  EXECUTED: 'neutral',
};

function adjustmentPercent(r: ContractRenewal): number | null {
  if (r.currentValue <= 0) return null;
  return ((r.proposedValue - r.currentValue) / r.currentValue) * 100;
}

export function ContractRenewalsPage(): JSX.Element {
  const { data, isLoading, error } = useRenewalPipeline();
  const createRenewal = useCreateRenewal();
  const approveRenewal = useApproveRenewal();
  const rejectRenewal = useRejectRenewal();

  const [showNew, setShowNew] = useState(false);
  const [showReject, setShowReject] = useState<ContractRenewal | null>(null);
  const [formError, setFormError] = useState('');

  /* Nova proposta */
  const [newContractId, setNewContractId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [indexName, setIndexName] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [proposedValue, setProposedValue] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [notes, setNotes] = useState('');

  /* Rejeição */
  const [rejectReason, setRejectReason] = useState('');

  const alerts = useMemo(() => data?.expiringAlerts ?? [], [data]);
  const pipeline = useMemo(() => data?.renewalPipeline ?? [], [data]);
  const financial = useMemo(() => data?.financialAdjustmentProposals ?? [], [data]);

  const buckets = useMemo(() => {
    const b = { le30: 0, le60: 0, le90: 0 };
    for (const a of alerts) {
      if (a.daysToExpiry <= 30) b.le30 += 1;
      if (a.daysToExpiry <= 60) b.le60 += 1;
      if (a.daysToExpiry <= 90) b.le90 += 1;
    }
    return b;
  }, [alerts]);

  const openNew = (): void => {
    setNewContractId(alerts[0]?.contractId ?? '');
    setVendorName('');
    setIndexName('');
    setCurrentValue('');
    setProposedValue('');
    setEffectiveDate('');
    setNotes('');
    setFormError('');
    setShowNew(true);
  };

  const submitNew = (): void => {
    if (!newContractId) {
      setFormError('Selecione o contrato da proposta.');
      return;
    }
    const cur = Number(currentValue);
    const prop = Number(proposedValue);
    if (!Number.isFinite(cur) || cur < 0) {
      setFormError('Valor atual deve ser um número ≥ 0.');
      return;
    }
    if (!Number.isFinite(prop) || prop < 0) {
      setFormError('Valor proposto deve ser um número ≥ 0.');
      return;
    }
    setFormError('');
    const payload: RenewalInput = {
      currentValue: cur,
      proposedValue: prop,
      ...(vendorName.trim() ? { vendorName: vendorName.trim() } : {}),
      ...(indexName.trim() ? { indexName: indexName.trim() } : {}),
      ...(effectiveDate ? { effectiveDate } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    createRenewal.mutate(
      { contractId: newContractId, ...payload },
      {
        onSuccess: () => setShowNew(false),
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  const openReject = (r: ContractRenewal): void => {
    setRejectReason('');
    setShowReject(r);
  };

  const submitReject = (): void => {
    if (!showReject) return;
    if (!rejectReason.trim()) {
      setFormError('Informe o motivo da rejeição.');
      return;
    }
    setFormError('');
    rejectRenewal.mutate(
      { renewalId: showReject.id, reason: rejectReason.trim() },
      { onSuccess: () => setShowReject(null) },
    );
  };

  if (isLoading) {
    return (
      <div className="p-xl">
        <LoadingSkeleton count={4} height="96px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-xl">
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">
            {(error as Error).message}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-xl flex flex-col gap-lg">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-md">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
            Fluxo de Renovação de Contratos
          </h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Alertas de expiração, pipeline de renovação e propostas de ajuste
            financeiro.
          </p>
        </div>
        <Button variant="primary" onClick={openNew}>
          Nova proposta
        </Button>
      </div>

      {/* Alertas de expiração */}
      <div className="grid grid-cols-1 gap-md sm:grid-cols-3">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Expira em até 30 dias
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-error">
            {buckets.le30}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            Ação crítica necessária
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Expira em até 60 dias
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {buckets.le60}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            Iniciar negociação
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Expira em até 90 dias
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {buckets.le90}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            Revisar escopo
          </p>
        </Card>
      </div>

      {/* Lista de alertas */}
      <Card padding="0">
        <div className="border-b border-outline-variant p-md">
          <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
            Expiration Alerts
          </h3>
        </div>
        {alerts.length === 0 ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Nenhum contrato ativo expirando nos próximos 90 dias.
          </p>
        ) : (
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {alerts.map((a) => (
              <li
                key={a.contractId}
                className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md last:border-b-0"
              >
                <div>
                  <p className="mb-xs mt-0 font-body-md text-body-md font-semibold text-on-surface">
                    {a.clientName ?? a.clientId}
                  </p>
                  <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                    {a.contractId} · expira {formatDate(a.endDate)} ·{' '}
                    {formatCurrency(a.value)}
                  </p>
                </div>
                <Badge
                  variant={a.daysToExpiry <= 30 ? 'error' : a.daysToExpiry <= 60 ? 'warning' : 'neutral'}
                >
                  {a.daysToExpiry} dia{a.daysToExpiry === 1 ? '' : 's'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Pipeline de renovação */}
      <div className="grid grid-cols-1 gap-lg xl:grid-cols-2">
        <Card padding="0">
          <div className="border-b border-outline-variant p-md">
            <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
              Renewal Pipeline
            </h3>
          </div>
          {pipeline.length === 0 ? (
            <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
              Nenhuma proposta em andamento.
            </p>
          ) : (
            <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
              {pipeline.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md last:border-b-0"
                >
                  <div>
                    <p className="mb-xs mt-0 font-body-md text-body-md font-semibold text-on-surface">
                      {r.vendorName ?? r.contractId}
                    </p>
                    <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                      {formatCurrency(r.currentValue)} →{' '}
                      <span className="font-semibold text-on-surface">
                        {formatCurrency(r.proposedValue)}
                      </span>
                      {r.indexName ? ` · ${r.indexName}` : ''}
                      {r.effectiveDate ? ` · vigência ${formatDate(r.effectiveDate)}` : ''}
                    </p>
                  </div>
                  <Badge variant={RENEWAL_STATUS_VARIANT[r.status] ?? 'neutral'}>
                    {RENEWAL_STATUS_LABEL[r.status] ?? r.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Propostas de ajuste financeiro */}
        <Card padding="0">
          <div className="border-b border-outline-variant p-md">
            <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
              Financial Adjustment Proposal
            </h3>
          </div>
          {financial.length === 0 ? (
            <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
              Nenhuma proposta de ajuste financeiro pendente.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left font-body-sm">
                <thead className="bg-surface font-label-sm text-on-surface-variant">
                  <tr>
                    <th className="p-md font-medium">Vendor / Contract</th>
                    <th className="p-md font-medium">Index</th>
                    <th className="p-md font-medium text-right">Valor atual</th>
                    <th className="p-md font-medium text-right">Ajuste %</th>
                    <th className="p-md font-medium text-right">Proposto</th>
                    <th className="p-md font-medium text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {financial.map((r) => {
                    const pct = adjustmentPercent(r);
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-outline-variant/60 hover:bg-surface-container-highest"
                      >
                        <td className="p-md">
                          <p className="mb-0 mt-0 font-body-md font-semibold text-on-surface">
                            {r.vendorName ?? r.contractId}
                          </p>
                          <p className="mb-0 mt-0 font-label-sm text-label-sm text-on-surface-variant">
                            {r.contractId}
                          </p>
                        </td>
                        <td className="p-md">{r.indexName ?? '—'}</td>
                        <td className="p-md text-right tabular-nums">
                          {formatCurrency(r.currentValue)}
                        </td>
                        <td
                          className={`p-md text-right tabular-nums ${
                            pct !== null && pct > 0 ? 'text-error' : 'text-success'
                          }`}
                        >
                          {pct !== null ? `${pct.toFixed(2)}%` : '—'}
                        </td>
                        <td className="p-md text-right font-label-md tabular-nums">
                          {formatCurrency(r.proposedValue)}
                        </td>
                        <td className="p-md">
                          <div className="flex items-center justify-center gap-sm">
                            <button
                              type="button"
                              className="font-label-md text-label-md text-success hover:underline"
                              disabled={approveRenewal.isPending}
                              onClick={() => approveRenewal.mutate(r.id)}
                            >
                              Aprovar
                            </button>
                            <button
                              type="button"
                              className="font-label-md text-label-md text-error hover:underline"
                              disabled={rejectRenewal.isPending}
                              onClick={() => openReject(r)}
                            >
                              Rejeitar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Modal nova proposta */}
      <Modal
        isOpen={showNew}
        onClose={() => setShowNew(false)}
        title="Nova proposta de renovação"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowNew(false)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={createRenewal.isPending} onClick={submitNew}>
              Criar proposta
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          {alerts.length > 0 ? (
            <div>
              <div className="field-label mb-xs">Contrato (expirando) *</div>
              <Select
                aria-label="Contrato"
                value={newContractId}
                onChange={(e) => setNewContractId(e.target.value)}
              >
                <option value="">Selecione…</option>
                {alerts.map((a) => (
                  <option key={a.contractId} value={a.contractId}>
                    {a.clientName ?? a.clientId} · {a.contractId} ({a.daysToExpiry}d)
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <Input
              id="renewal-contractid"
              label="Contrato (ID) *"
              value={newContractId}
              onChange={(e) => setNewContractId(e.target.value)}
              placeholder="Ex.: ctr-123…"
            />
          )}
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            <Input
              id="renewal-vendor"
              label="Vendor / nome"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder="Ex.: Oracle"
            />
            <Input
              id="renewal-index"
              label="Índice de reajuste"
              value={indexName}
              onChange={(e) => setIndexName(e.target.value)}
              placeholder="IPCA, IGP-M…"
            />
            <Input
              id="renewal-current"
              label="Valor atual (R$) *"
              type="number"
              min="0"
              step="0.01"
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
            />
            <Input
              id="renewal-proposed"
              label="Valor proposto (R$) *"
              type="number"
              min="0"
              step="0.01"
              value={proposedValue}
              onChange={(e) => setProposedValue(e.target.value)}
            />
            <Input
              id="renewal-effective"
              label="Vigência"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
          <Input
            id="renewal-notes"
            label="Observações"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex.: reajuste de 4,12% pelo IPCA acumulado"
          />
          {formError ? (
            <p className="mb-0 font-body-sm text-body-sm text-error">{formError}</p>
          ) : null}
        </div>
      </Modal>

      {/* Modal rejeitar */}
      <Modal
        isOpen={showReject !== null}
        onClose={() => setShowReject(null)}
        title="Rejeitar proposta de renovação"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowReject(null)}>
              Cancelar
            </Button>
            <Button variant="danger" loading={rejectRenewal.isPending} onClick={submitReject}>
              Rejeitar proposta
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {showReject?.vendorName ?? showReject?.contractId} ·{' '}
            {showReject ? formatCurrency(showReject.currentValue) : ''} →{' '}
            {showReject ? formatCurrency(showReject.proposedValue) : ''}
          </p>
          <Input
            id="reject-reason"
            label="Motivo (obrigatório)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex.: índice não aplicável; renegociar condições"
          />
          {formError ? (
            <p className="mb-0 font-body-sm text-body-sm text-error">{formError}</p>
          ) : null}
        </div>
      </Modal>

      {/* Nota de índices (estático, ilustra a tela) */}
      <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
        Ajustes baseados em índices (IPCA, IGP-M) são aplicados na proposta;
        o percentual é calculado sobre o valor atual do contrato.
      </p>
    </div>
  );
}